'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { VStack } from '@terra/ui-styled-system';
import {
  AddCredentialsDialog,
  Button,
  CredentialCard,
  CredentialDeleteConfirmationDialog,
  CredentialVerificationStreamModals,
  CredentialsEmptyState,
  Skeleton,
} from '@terra/ui-design-system';
import { Add01Icon } from '@terra/ui-icons';
import type { ApplicationRoleDto } from '@terra/shared-contracts-applications';
import { FEATURE_ANALYSIS_CREDENTIAL_REMOVAL_ENABLED_FLAG } from '@terra/shared-contracts-feature-flags';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import { CredentialsCopilotPendingEditsPanel } from '../../object-context/CredentialsCopilotPendingEditsPanel';
import type { useSSE } from '../../../app/providers/SseProvider';
import { useSettingsTabHeaderActions } from '../settingsTabHeaderContext';
import type { BackendCredential } from './applicationCredential.transforms';
import type { useTargetCredentialActions } from './useTargetCredentialActions';
import type { useTargetRoleActions } from './useTargetRoleActions';
import { useTargetCredentialList } from './useTargetCredentialList';
import { TargetNoTargetState } from './TargetNoTargetState';

type TargetCredentialsPanelProps = {
  hasProjectSelected: boolean;
  isLoading: boolean;
  isReadOnly?: boolean;
  applicationId: string | undefined;
  credentials: BackendCredential[];
  credentialActions: ReturnType<typeof useTargetCredentialActions>;
  roleActions: ReturnType<typeof useTargetRoleActions>;
  refetchRoles: () => Promise<unknown>;
  subscribe: ReturnType<typeof useSSE>['subscribe'];
  customRoles: ApplicationRoleDto[];
};

type TargetCredentialsListProps = Omit<
  TargetCredentialsPanelProps,
  'hasProjectSelected' | 'applicationId'
> & { applicationId: string };

function TargetCredentialsList({
  isLoading,
  isReadOnly,
  applicationId,
  credentials,
  credentialActions,
  roleActions,
  refetchRoles,
  subscribe,
  customRoles,
}: TargetCredentialsListProps) {
  const tCommon = useTranslations('designSystem.common');
  const tCredentials = useTranslations('designSystem.credentials');
  const list = useTargetCredentialList({
    applicationId,
    credentials,
    credentialActions,
    roleActions,
    refetchRoles,
  });
  const {
    credentialsList,
    handleTestAllCredentials,
    isTestingAllCredentials,
    openCreateCredentialDialog,
    testingCredentialIds,
  } = list;
  const { isFeatureFlagEnabled: isFeatureAnalysisCredentialRemovalEnabled } =
    useFeatureFlags(FEATURE_ANALYSIS_CREDENTIAL_REMOVAL_ENABLED_FLAG);
  const hasCredentials = credentialsList.length > 0;
  const isAnyCredentialTesting = testingCredentialIds.size > 0;

  const headerActions = useMemo(() => {
    if (!hasCredentials) {
      return null;
    }

    return (
      <>
        <Button
          size="xs"
          variant="ghost"
          onClick={handleTestAllCredentials}
          disabled={
            isTestingAllCredentials || isAnyCredentialTesting || isReadOnly
          }
          loading={isTestingAllCredentials}
        >
          {tCommon('testAll')}
        </Button>
        <Button
          size="xs"
          variant="outline"
          disabled={isReadOnly}
          onClick={() => {
            if (!isReadOnly) {
              openCreateCredentialDialog();
            }
          }}
        >
          <Add01Icon size={16} />
          {tCommon('add')}
        </Button>
      </>
    );
  }, [
    handleTestAllCredentials,
    hasCredentials,
    isAnyCredentialTesting,
    isReadOnly,
    isTestingAllCredentials,
    openCreateCredentialDialog,
    tCommon,
  ]);

  useSettingsTabHeaderActions(headerActions);

  return (
    <>
      <VStack alignItems="stretch" gap={4}>
        <CredentialsCopilotPendingEditsPanel
          credentials={credentials.map((credential) => ({
            id: credential.id,
            username: credential.username,
            role: credential.role,
            roleId: credential.roleId ?? null,
            roleDescription: credential.roleDescription ?? null,
            headers: credential.headers,
            cookies: credential.cookies,
          }))}
          onPendingEditsAccepted={(acceptedByCredentialId) => {
            void list.persistAcceptedPendingEdits(acceptedByCredentialId);
          }}
        />
        {isLoading ? (
          <Skeleton height="14" borderRadius="md" />
        ) : hasCredentials ? (
          <VStack alignItems="stretch" gap={3}>
            {credentialsList.map((credential) => (
              <CredentialCard
                key={credential.id}
                applicationId={applicationId}
                credential={credential}
                dataSources={list.dataSources}
                onEdit={list.handleEditCredential}
                onDelete={
                  isReadOnly ||
                  (credential.generatedBy === 'FEATURE_ANALYSIS' &&
                    !isFeatureAnalysisCredentialRemovalEnabled)
                    ? undefined
                    : list.requestDeleteCredential
                }
                onTest={list.handleTestCredential}
                onAbort={isReadOnly ? undefined : list.handleAbortCredential}
                onDisable={list.handleDisableCredential}
                isTesting={
                  (isTestingAllCredentials && !credential.isDisabled) ||
                  testingCredentialIds.has(credential.id)
                }
                isTestingAllCredentials={isTestingAllCredentials}
                isReadOnly={isReadOnly}
                verificationStreamingId={list.getVerificationStreamingIdForDisplay(
                  credential.id,
                )}
                onOpenVerificationStream={list.openVerificationStream}
              />
            ))}
          </VStack>
        ) : (
          <CredentialsEmptyState
            title={tCredentials('emptyState.emptyStateTitle')}
            description={tCredentials('emptyState.emptyStateDescription')}
            buttonText={tCredentials('addCredential')}
            onButtonClick={openCreateCredentialDialog}
            isButtonDisabled={isReadOnly}
          />
        )}
      </VStack>

      <AddCredentialsDialog
        applicationId={applicationId}
        open={list.isDialogOpen}
        onOpenChange={list.handleDialogOpenChange}
        onSaveCredential={list.handleSaveCredential}
        initialCredential={list.editingCredential}
        customRoles={customRoles}
        dataSources={list.dataSources}
      />
      <CredentialVerificationStreamModals
        credentials={credentialsList}
        streamingIdByCredentialId={list.streamingIdByCredentialId}
        testingCredentialIds={testingCredentialIds}
        openStreamCredentialId={list.openStreamCredentialId}
        onOpenStreamCredentialIdChange={list.setOpenStreamCredentialId}
        onStreamStarted={list.markVerificationStreamStarted}
        onSSESubscribe={subscribe}
      />
      <CredentialDeleteConfirmationDialog
        credential={list.deletingCredential}
        isDeleting={list.isDeletingCredential}
        onCancel={list.clearDeletingCredential}
        onConfirm={list.handleDeleteCredential}
      />
    </>
  );
}

export function TargetCredentialsPanel({
  hasProjectSelected,
  applicationId,
  ...listProps
}: TargetCredentialsPanelProps) {
  if (!hasProjectSelected || !applicationId) {
    return <TargetNoTargetState descriptionKey="credentials" />;
  }

  return <TargetCredentialsList applicationId={applicationId} {...listProps} />;
}
